import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { EvalSet, Integration, KnowledgeBase, McpServer, Skill } from '@orqforge/shared';
import { apiClient } from '../lib/api-client';
import { Badge } from '../components/ui/Badge';

function readApiError(err: unknown): string {
  if (!(err instanceof Error) || !err.message.trim()) return 'Could not update agent access.';
  try {
    const body = JSON.parse(err.message) as { message?: string | string[] };
    if (typeof body.message === 'string' && body.message.trim()) return body.message;
    if (Array.isArray(body.message) && body.message[0]) return body.message.join(', ');
  } catch {
    return err.message;
  }
  return err.message;
}

function statusVariant(status: string): 'success' | 'destructive' | 'secondary' | 'warning' {
  if (status === 'connected') return 'success';
  if (status === 'error') return 'destructive';
  if (status === 'disconnected') return 'secondary';
  return 'warning';
}

function integrationToolCount(item: Integration): number | null {
  const extra = item as Integration & { toolCount?: number; tools?: unknown[] };
  const n = extra.toolCount ?? extra.tools?.length;
  return typeof n === 'number' && n > 0 ? n : null;
}

function cardClass(on: boolean) {
  return on
    ? 'border-blue-600 bg-blue-50 ring-1 ring-blue-600/60 dark:bg-blue-950/40'
    : 'border-gray-200 hover:border-blue-400 dark:border-gray-700';
}

export function AgentDetailAccess({
  agentId,
  onError,
}: {
  agentId: string;
  onError: (message: string | null) => void;
}) {
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [mcp, setMcp] = useState<McpServer[]>([]);
  const [skills, setSkills] = useState<Skill[]>([]);
  const [knowledge, setKnowledge] = useState<KnowledgeBase[]>([]);
  const [evals, setEvals] = useState<EvalSet[]>([]);
  const [assignedIntegrations, setAssignedIntegrations] = useState<Set<string>>(new Set());
  const [assignedMcp, setAssignedMcp] = useState<Set<string>>(new Set());
  const [assignedSkills, setAssignedSkills] = useState<Set<string>>(new Set());
  const [assignedKnowledge, setAssignedKnowledge] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    void apiClient.listIntegrations().then(setIntegrations).catch(() => setIntegrations([]));
    void apiClient.listMcpServers().then(setMcp).catch(() => setMcp([]));
    void apiClient.listSkills().then((r) => setSkills(r.items)).catch(() => setSkills([]));
    void apiClient.listKnowledgeBases().then(setKnowledge).catch(() => setKnowledge([]));
    void apiClient.agentIntegrations(agentId).then((rows) => setAssignedIntegrations(new Set(rows.map((r) => r.id)))).catch(() => setAssignedIntegrations(new Set()));
    void apiClient.agentMcpServers(agentId).then((rows) => setAssignedMcp(new Set(rows.map((r) => r.id)))).catch(() => setAssignedMcp(new Set()));
    void apiClient.agentSkills(agentId).then((rows) => setAssignedSkills(new Set(rows.map((r) => r.id)))).catch(() => setAssignedSkills(new Set()));
    void apiClient.agentKnowledgeBases(agentId).then((rows) => setAssignedKnowledge(new Set(rows.map((r) => r.id)))).catch(() => setAssignedKnowledge(new Set()));
    void apiClient.listEvalSets().then((rows) => setEvals(rows.filter((s) => s.agentId === agentId))).catch(() => setEvals([]));
  }, [agentId]);

  async function toggle(
    id: string,
    assignedSet: Set<string>,
    assign: () => Promise<unknown>,
    unassign: () => Promise<unknown>,
    update: (next: Set<string>) => void,
  ) {
    if (busy) return;
    const on = assignedSet.has(id);
    onError(null);
    setBusy(id);
    try {
      if (on) await unassign();
      else await assign();
      const next = new Set(assignedSet);
      if (on) next.delete(id);
      else next.add(id);
      update(next);
    } catch (err) {
      onError(readApiError(err));
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <section className="mt-4 rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold">Integration access</h2>
          <span className="text-xs text-gray-400">{assignedIntegrations.size} of {integrations.length} enabled</span>
        </div>
        <p className="mt-2 text-sm text-gray-500">Click an integration to grant or revoke this agent's access.</p>
        <div className="mt-3 grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fill,minmax(220px,1fr))' }}>
          {integrations.length === 0 ? (
            <p className="text-sm text-gray-500">No integrations registered yet. Create them on the Integrations page.</p>
          ) : integrations.map((item) => {
            const on = assignedIntegrations.has(item.id);
            const count = integrationToolCount(item);
            return (
              <button
                key={item.id}
                type="button"
                disabled={busy === item.id}
                className={`flex items-center gap-3 rounded-xl border p-3 text-left ${cardClass(on)}`}
                onClick={() => void toggle(item.id, assignedIntegrations, () => apiClient.assignIntegration(agentId, item.id), () => apiClient.unassignIntegration(agentId, item.id), setAssignedIntegrations)}
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">{item.name}</span>
                  <span className="block truncate text-xs text-gray-400">{item.key}</span>
                  <Badge className="mt-1" variant={statusVariant(item.status)}>{item.status}</Badge>
                  {count !== null && <span className="mt-1 block text-[11px] text-gray-400">{count} tools included</span>}
                </span>
                <span className="text-xs text-blue-600">{busy === item.id ? '...' : on ? 'On' : 'Add'}</span>
              </button>
            );
          })}
        </div>
      </section>

      <section className="mt-4 rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold">MCP server access</h2>
          <span className="text-xs text-gray-400">{assignedMcp.size} of {mcp.length} enabled</span>
        </div>
        <p className="mt-2 text-sm text-gray-500">
          Click an MCP server to grant or revoke this agent's access. All tools from enabled servers are auto-discovered. No per-tool configuration is needed.
        </p>
        <div className="mt-3 grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fill,minmax(220px,1fr))' }}>
          {mcp.length === 0 ? (
            <p className="text-sm text-gray-500">No MCP servers registered yet. Add them on the MCP Servers page.</p>
          ) : mcp.map((item) => {
            const on = assignedMcp.has(item.id);
            return (
              <button
                key={item.id}
                type="button"
                disabled={busy === item.id}
                className={`flex items-center gap-3 rounded-xl border p-3 text-left ${cardClass(on)}`}
                onClick={() => void toggle(item.id, assignedMcp, () => apiClient.assignMcpServer(agentId, item.id), () => apiClient.unassignMcpServer(agentId, item.id), setAssignedMcp)}
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">{item.name}</span>
                  <span className="block truncate text-xs text-gray-400">{item.transport} @ {item.url || 'stdio'}</span>
                  {!item.enabled && <Badge className="mt-1" variant="secondary">disabled</Badge>}
                </span>
                <span className="text-xs text-blue-600">{busy === item.id ? '...' : on ? 'On' : 'Add'}</span>
              </button>
            );
          })}
        </div>
      </section>

      <section className="mt-4 rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold">Skill access</h2>
          <span className="text-xs text-gray-400">{assignedSkills.size} of {skills.length} enabled</span>
        </div>
        <p className="mt-2 text-sm text-gray-500">
          Skills are reusable prompt fragments injected into the agent's system prompt. Click a skill to attach or detach it from this agent.
        </p>
        <div className="mt-3 grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fill,minmax(240px,1fr))' }}>
          {skills.length === 0 ? (
            <p className="text-sm text-gray-500">No skills defined yet. Create them on the Skills page.</p>
          ) : skills.map((item) => {
            const on = assignedSkills.has(item.id);
            return (
              <button
                key={item.id}
                type="button"
                disabled={busy === item.id}
                className={`flex items-start gap-3 rounded-xl border p-3 text-left ${cardClass(on)}`}
                onClick={() => void toggle(item.id, assignedSkills, () => apiClient.assignSkill(agentId, item.id), () => apiClient.unassignSkill(agentId, item.id), setAssignedSkills)}
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">{item.name}</span>
                  <span className="line-clamp-2 text-xs text-gray-400">{item.instructions}</span>
                </span>
                <span className="text-xs text-blue-600">{busy === item.id ? '...' : on ? 'On' : 'Add'}</span>
              </button>
            );
          })}
        </div>
      </section>

      <section className="mt-4 rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold">Knowledge base access</h2>
          <span className="text-xs text-gray-400">{assignedKnowledge.size} of {knowledge.length} enabled</span>
        </div>
        <p className="mt-2 text-sm text-gray-500">
          Click a knowledge base to attach or detach it from this agent. Attached knowledge bases are searched at chat time and relevant chunks are injected into the system prompt for RAG.
        </p>
        <div className="mt-3 grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fill,minmax(220px,1fr))' }}>
          {knowledge.length === 0 ? (
            <p className="text-sm text-gray-500">No knowledge bases defined yet. Create them on the Knowledge page.</p>
          ) : knowledge.map((item) => {
            const on = assignedKnowledge.has(item.id);
            return (
              <button
                key={item.id}
                type="button"
                disabled={busy === item.id}
                className={`flex items-start gap-3 rounded-xl border p-3 text-left ${cardClass(on)}`}
                onClick={() => void toggle(item.id, assignedKnowledge, () => apiClient.assignKnowledgeBase(agentId, item.id), () => apiClient.unassignKnowledgeBase(agentId, item.id), setAssignedKnowledge)}
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">{item.name}</span>
                  {item.description && <span className="line-clamp-2 text-xs text-gray-400">{item.description}</span>}
                  <span className="mt-1 block text-[0.68rem] text-gray-400">{item.embeddingModel} · chunks {item.chunkSize}/{item.chunkOverlap}</span>
                </span>
                <span className="text-xs text-blue-600">{busy === item.id ? '...' : on ? 'On' : 'Add'}</span>
              </button>
            );
          })}
        </div>
      </section>

      <section className="mt-4 rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold">Evaluations</h2>
          <span className="text-xs text-gray-400">{evals.length} eval set(s)</span>
        </div>
        <p className="mt-2 text-sm text-gray-500">
          Golden-case test sets that gate config changes. Prompt, rule, triage, and KB changes are eval-gated. If the pass rate drops below threshold, the change is reverted.
        </p>
        {evals.length > 0 ? (
          <div className="mt-3 grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fill,minmax(200px,1fr))' }}>
            {evals.map((es) => (
              <Link key={es.id} to="/evals" className="rounded-xl border border-gray-200 p-3 hover:border-blue-400 dark:border-gray-700">
                <div className="text-sm font-medium">{es.name}</div>
                <div className="text-xs text-gray-400">threshold {es.passThreshold}</div>
              </Link>
            ))}
          </div>
        ) : (
          <Link to="/evals" className="mt-3 inline-flex"><span className="rounded-md border border-gray-300 px-3 py-1.5 text-sm dark:border-gray-600">Go to Evals</span></Link>
        )}
      </section>
    </>
  );
}
